import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { assetKeys } from '@/shared/lib/query/queryKeys';
import type { DomainEditHook } from '@/shared/types/editHooks';

import type { AssetBaseInfo } from '@/features/asset-detail/api';
import { useDomainTree, useUpdateAssetDomains } from '@/features/asset-detail/model';

type UseAssetDomainEditProps = {
  assetId: string;
  entityType: string;
  serviceType?: string;
  fqn: string;
};

export const useAssetDomainEdit = ({
  assetId,
  entityType,
  serviceType,
  fqn,
}: UseAssetDomainEditProps): DomainEditHook => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);

  const queryKey = useMemo(() => assetKeys.assetBaseInfo(entityType, fqn), [entityType, fqn]);

  const assetData = queryClient.getQueryData<AssetBaseInfo>(queryKey);

  const currentDomains = useMemo(() => assetData?.domains || [], [assetData?.domains]);

  const { tree, domains, isLoading: isLoadingDomains } = useDomainTree();
  const { mutateAsync: updateDomainsAsync, isPending: isUpdating } = useUpdateAssetDomains(
    entityType,
    serviceType,
    assetId,
    fqn
  );

  const currentDomainId = useMemo(() => (currentDomains.length > 0 ? currentDomains[0]?.id : null), [currentDomains]);

  useEffect(() => {
    setSelectedDomainId(currentDomainId);
  }, [currentDomainId]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    const latestAssetData = queryClient.getQueryData<AssetBaseInfo>(queryKey);
    const latestDomains = latestAssetData?.domains || [];
    const latestDomainId = latestDomains.length > 0 ? latestDomains[0]?.id : null;
    setSelectedDomainId(latestDomainId);
  }, [queryClient, queryKey]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    const latestAssetData = queryClient.getQueryData<AssetBaseInfo>(queryKey);
    const latestDomains = latestAssetData?.domains || [];
    const latestDomainId = latestDomains.length > 0 ? latestDomains[0]?.id : null;
    setSelectedDomainId(latestDomainId);
  }, [queryClient, queryKey]);

  const handleSelect = useCallback(
    (keys: React.Key[]) => {
      const newKey = keys.length > 0 ? String(keys[0]) : null;

      if (newKey === selectedDomainId) {
        setSelectedDomainId(null);
      } else {
        setSelectedDomainId(newKey);
      }
    },
    [selectedDomainId]
  );

  const handleSave = useCallback(
    async (selectedDomainIds: string[]) => {
      const latestAssetData = queryClient.getQueryData<AssetBaseInfo>(queryKey);
      const latestDomains = latestAssetData?.domains || [];
      const domainId = selectedDomainIds.length > 0 ? selectedDomainIds[0] : null;

      await updateDomainsAsync({
        selectedDomainId: domainId,
        domains,
        currentDomains: latestDomains,
      });
    },
    [domains, queryClient, queryKey, updateDomainsAsync]
  );

  const hasChanges = useMemo(() => {
    return currentDomainId !== selectedDomainId;
  }, [currentDomainId, selectedDomainId]);

  return {
    isOpen,
    selectedDomainId,
    treeData: tree,
    isLoadingDomains,
    isUpdating,
    hasChanges,
    handleOpen,
    handleClose,
    handleSelect,
    handleSave,
  };
};
